import React, { useState } from 'react';
import { MapPin, DollarSign, Star, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../lib/utils';

const BREAKDOWN_LABELS = {
  competences: 'Compétences',
  experience: 'Expérience',
  coherence_salariale: 'Salaire',
  disponibilite: 'Disponibilité',
  match_global: 'Match',
};

const CandidateMiniCard = ({ 
  candidate, 
  status,
  score,
  aiBreakdown,
  aiResume,
  actions = [],
  disabled = false,
  compact = false,
  onClick
}) => {
  const [showBreakdown, setShowBreakdown] = useState(false);

  const getScoreColor = (s) => {
    if (s >= 75) return 'text-green-600 bg-green-50';
    if (s >= 55) return 'text-orange-600 bg-orange-50';
    return 'text-gray-600 bg-gray-50';
  };

  const getBarColor = (s) => {
    if (s >= 75) return 'bg-green-500';
    if (s >= 55) return 'bg-orange-400';
    return 'bg-gray-400';
  };

  return (
    <div 
      className={cn(
        "bg-white rounded-lg border border-gray-200 p-3 transition-all",
        onClick && "cursor-pointer hover:border-gray-300 hover:shadow-sm",
        disabled && "opacity-60"
      )}
      onClick={disabled ? undefined : onClick}
      data-testid={`mini-card-${candidate.id}`}
    >
      {/* Header: Nom + Score */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-gray-900 truncate">
            {candidate.first_name} {candidate.last_name}
          </h4>
          <p className="text-sm text-gray-500 truncate">
            {candidate.main_profession || candidate.role || 'Non défini'}
          </p>
        </div>
        {score !== undefined && score !== null && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (aiBreakdown) setShowBreakdown(!showBreakdown);
            }}
            className={cn(
              "flex items-center gap-1 px-2 py-1 rounded text-xs font-bold ml-2 transition-colors",
              getScoreColor(score),
              aiBreakdown && "cursor-pointer hover:ring-1 hover:ring-gray-300"
            )}
            data-testid={`score-badge-${candidate.id}`}
          >
            <Star className="h-3 w-3" />
            {score}
            {aiBreakdown && (
              showBreakdown ? <ChevronUp className="h-3 w-3 ml-0.5" /> : <ChevronDown className="h-3 w-3 ml-0.5" />
            )}
          </button>
        )}
      </div>

      {/* AI Breakdown (collapsible) */}
      {showBreakdown && aiBreakdown && (
        <div className="mb-3 p-2 bg-gray-50 rounded-lg" data-testid={`breakdown-${candidate.id}`}>
          {aiResume && (
            <p className="text-xs text-gray-600 italic mb-2">{aiResume}</p>
          )}
          <div className="space-y-1.5">
            {Object.entries(BREAKDOWN_LABELS).map(([key, label]) => {
              const val = aiBreakdown[key];
              if (val === undefined) return null;
              return (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-24 flex-shrink-0">{label}</span>
                  <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className={cn("h-full rounded-full transition-all", getBarColor(val))} style={{ width: `${val}%` }} />
                  </div>
                  <span className="text-xs font-medium text-gray-600 w-7 text-right">{val}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Contexte: Pays + Salaire */}
      {!compact && (
        <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
          <span className="flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {candidate.country || '-'}
          </span>
          <span className="flex items-center gap-1">
            <DollarSign className="h-3 w-3" />
            {candidate.expected_salary || candidate.salary || '-'}€
          </span>
        </div>
      )}

      {/* Actions */}
      {actions.length > 0 && (
        <div className="flex gap-2 mt-2">
          {actions.map((action, idx) => (
            <button
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) action.onClick();
              }}
              disabled={disabled || action.disabled}
              className={cn(
                "flex-1 text-xs font-medium py-2 px-3 rounded transition-colors",
                action.variant === 'primary' && "bg-tp-primary text-white hover:bg-tp-primary-light",
                action.variant === 'success' && "bg-green-500 text-white hover:bg-green-600",
                action.variant === 'danger' && "bg-red-50 text-red-700 hover:bg-red-100",
                action.variant === 'secondary' && "bg-gray-100 text-gray-700 hover:bg-gray-200",
                !action.variant && "bg-gray-100 text-gray-700 hover:bg-gray-200",
                (disabled || action.disabled) && "opacity-50 cursor-not-allowed"
              )}
              data-testid={`action-${action.id || idx}`}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CandidateMiniCard;
